import { useState, useEffect, useCallback, useRef } from 'react';

interface SSEPost {
  _id: string;
  content: string;
  user: {
    name: string;
    handle: string;
    picture: string;
  };
  createdAt: string;
  updatedAt: string;
  likeCount: number;
  isLiked: boolean;
  commentCount: number;
}

interface SSEMessage {
  type: 'new_post' | 'post_update' | 'new_comment' | 'post_deleted' | 'heartbeat';
  data?: SSEPost | {
    postId: string;
    comment: {
      _id: string;
      content: string;
      user: {
        name: string;
        handle: string;
        picture: string;
      };
      createdAt: string;
      updatedAt: string;
    };
  } | {
    postId: string;
  };
}

interface UseSmartSSEProps {
  onNewPost: (post: SSEPost) => void;
  onPostUpdate: (post: SSEPost) => void;
  onNewComment?: (postId: string, comment: any) => void;
  onPostDeleted?: (postId: string) => void;
  feedType: 'following' | 'all';
  onReconnect?: (inactiveTimestamp?: Date | null) => void; // Callback to refresh feed when reconnecting
}

export const useSmartSSE = ({ onNewPost, onPostUpdate, onNewComment, onPostDeleted, feedType, onReconnect }: UseSmartSSEProps) => {
  const [isActive, setIsActive] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const eventSourceRef = useRef<EventSource | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const isActiveRef = useRef(isActive);
  const wasInactiveRef = useRef(false); // Track if user was previously inactive
  const inactiveTimestampRef = useRef<Date | null>(null); // Track when user became inactive

  // Update ref when isActive changes
  useEffect(() => {
    isActiveRef.current = isActive;
    
    // Track when user becomes inactive
    if (!isActive && wasInactiveRef.current === false) {
      inactiveTimestampRef.current = new Date();
    }
    
    // If user was inactive and now becomes active, trigger reconnect callback
    if (isActive && wasInactiveRef.current && onReconnect) {
      const inactiveTimestamp = inactiveTimestampRef.current;
      onReconnect(inactiveTimestamp);
      inactiveTimestampRef.current = null; // Reset after use
    }
    
    // Update was inactive flag
    wasInactiveRef.current = !isActive;
  }, [isActive, onReconnect]);

  // Activity detection
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    if (!isActiveRef.current) {
      setIsActive(true);
    }
    
    // Set user as inactive after 5 minutes of no interaction
    inactivityTimerRef.current = setTimeout(() => {
      setIsActive(false);
    }, 5 * 60 * 1000);
  }, []); // No dependencies needed now

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsActive(false);
      } else {
        resetInactivityTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [resetInactivityTimer]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const activityHandler = (event: Event) => {
      resetInactivityTimer();
    };
    
    events.forEach(event => {
      document.addEventListener(event, activityHandler, { passive: true });
    });

    resetInactivityTimer(); // Start the timer

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, activityHandler);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  // SSE connection management
  useEffect(() => {
    if (isActive && !eventSourceRef.current) {
      // Open SSE connection
      const sessionToken = localStorage.getItem('session_token');
      const url = new URL(`${process.env.REACT_APP_API_URL}/api/posts/stream`);
      url.searchParams.append('feedType', feedType);
      
      if (sessionToken) {
        url.searchParams.append('token', sessionToken);
      }

      const eventSource = new EventSource(url.toString());
      eventSourceRef.current = eventSource;
      setConnectionStatus('connected');
      reconnectAttemptsRef.current = 0;

      eventSource.onopen = () => {
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          const message: SSEMessage = JSON.parse(event.data);
          
          switch (message.type) {
            case 'new_post':
              if (message.data && 'content' in message.data) {
                onNewPost(message.data as SSEPost);
              }
              break;
            case 'post_update':
              if (message.data && 'content' in message.data) {
                onPostUpdate(message.data as SSEPost);
              }
              break;
            case 'new_comment':
              if (message.data && 'postId' in message.data && onNewComment) {
                const commentData = message.data as { postId: string; comment: any };
                onNewComment(commentData.postId, commentData.comment);
              }
              break;
            case 'post_deleted':
              if (message.data && 'postId' in message.data && onPostDeleted) {
                const deletionData = message.data as { postId: string };
                onPostDeleted(deletionData.postId);
              }
              break;
            case 'heartbeat':
              // Keep connection alive
              break;
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      eventSource.onerror = () => {
        console.error('SSE connection error');
        setConnectionStatus('reconnecting');
        
        // Close current connection
        eventSource.close();
        eventSourceRef.current = null;
        
        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts && isActive) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current++;
          
          setTimeout(() => {
            if (isActive && !eventSourceRef.current) {
              // This will trigger the useEffect again to create a new connection
              setConnectionStatus('disconnected');
            }
          }, delay);
        } else {
          setConnectionStatus('disconnected');
        }
      };

    } else if (!isActive && eventSourceRef.current) {
      // Close SSE connection when inactive
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setConnectionStatus('disconnected');
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [isActive, onNewPost, onPostUpdate, onNewComment, onPostDeleted, feedType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);

  const forceReconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    setIsActive(true);
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  return {
    isActive,
    connectionStatus,
    forceReconnect
  };
};
